import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createShift, listShifts, updateShift } from '../../api/hr';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
  Pagination,
} from '../../components/common';
import type { Shift } from '../../types/domain';

/** hh:mm:ss (or hh:mm) -> "22:30" for display. */
function fmtTime(t: string): string {
  return t.slice(0, 5);
}

function shiftWindowLabel(shift: Shift): string {
  const window = `${fmtTime(shift.start_time)} → ${fmtTime(shift.end_time)}`;
  return shift.crosses_midnight ? `${window} (lintas hari)` : window;
}

/**
 * Shift Master (FASE 9 closure correction, migration 0042). Schedule
 * authoring is part of FASE 9's canonical scope per V5 — this page is the
 * minimum operational Shift Master the owner required: name/code,
 * start/end time, active state, branch scope, cross-midnight handling.
 * crosses_midnight is derived server-side (fn_create_shift_checked /
 * fn_update_shift_checked) from start_time/end_time — an overnight shift
 * like 22:30 -> 06:30 is a real shift, never rejected as "invalid".
 *
 * Branch-scoped: re-fetches on Cabang Aktif change, matching Attendance's
 * pattern and the owner's branch-security requirement.
 */
export default function ShiftsPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId, branches } = useBranch();
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selected, setSelected] = useState<Shift | null>(null);

  const canManage = hasPermission('shift', 'create');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listShifts(activeBranchId ?? undefined);
      setShifts(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return shifts.filter((s) => {
      if (q && !s.name.toLowerCase().includes(q) && !s.shift_code.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && s.status !== statusFilter) return false;
      return true;
    });
  }, [shifts, search, statusFilter]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  const activeCount = shifts.filter((s) => s.status === 'ACTIVE').length;
  const crossMidnightCount = shifts.filter((s) => s.crosses_midnight).length;

  return (
    <div>
      <PageHeader title="Shift Master" subtitle="Definisi shift kerja — dasar penjadwalan (Schedule). Migrasi 0042." />
      {canManage && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
          {showForm ? 'Batal' : '+ Shift baru'}
        </Button>
      )}
      {showForm && canManage && activeBranchId && (
        <NewShiftForm
          branchId={activeBranchId}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={[
              { label: 'Total Shift', value: shifts.length },
              { label: 'Aktif', value: activeCount, tone: 'success' as const },
              { label: 'Lintas Hari', value: crossMidnightCount, tone: 'info' as const },
            ]}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari kode/nama shift..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 140 }}>
                <option value="ALL">Semua status</option>
                <option value="ACTIVE">Aktif</option>
                <option value="INACTIVE">Nonaktif</option>
              </Select>
            }
          />
          {shifts.length === 0 && <EmptyState text="Belum ada shift untuk cabang ini." />}
          {shifts.length > 0 && total === 0 && <EmptyState text="Tidak ada shift yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Kode</th>
                  <th style={thStyle}>Nama</th>
                  <th style={thStyle}>Jam Kerja</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((s) => (
                  <ClickableRow key={s.shift_id} onClick={() => setSelected(s)}>
                    <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{s.shift_code}</td>
                    <td style={tdStyle}>{s.name}</td>
                    <td style={tdStyle}>{shiftWindowLabel(s)}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={s.status} />
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
      <ShiftDetailDrawer
        shift={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        branches={branches}
        canEdit={canManage}
        onChanged={() => void load()}
      />
    </div>
  );
}

function NewShiftForm({ branchId, onCreated }: { branchId: string; onCreated: () => void }) {
  const [shiftCode, setShiftCode] = useState('');
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const crossesMidnight = endTime <= startTime;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await createShift(branchId, shiftCode.trim(), name.trim(), startTime, endTime);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ width: 130 }}>
        <FieldLabel label="Kode Shift">
          <TextInput value={shiftCode} onChange={(e) => setShiftCode(e.target.value)} required autoFocus placeholder="SH-PAGI" />
        </FieldLabel>
      </div>
      <div style={{ flex: 1, minWidth: 180 }}>
        <FieldLabel label="Nama Shift">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} required placeholder="Shift Pagi" />
        </FieldLabel>
      </div>
      <div style={{ width: 130 }}>
        <FieldLabel label="Jam Mulai">
          <TextInput type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 130 }}>
        <FieldLabel label="Jam Selesai">
          <TextInput type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      {crossesMidnight && (
        <div style={{ width: '100%', fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-info)' }}>
          Shift lintas hari terdeteksi ({startTime} → {endTime}) — akan disimpan sebagai shift lintas hari, bukan kesalahan input.
        </div>
      )}
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function ShiftDetailDrawer({
  shift,
  open,
  onClose,
  branches,
  canEdit,
  onChanged,
}: {
  shift: Shift | null;
  open: boolean;
  onClose: () => void;
  branches: { branch_id: string; name: string }[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [name, setName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [status, setStatus] = useState<Shift['status']>('ACTIVE');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shift) return;
    setName(shift.name);
    setStartTime(fmtTime(shift.start_time));
    setEndTime(fmtTime(shift.end_time));
    setStatus(shift.status);
    setError(null);
  }, [shift?.shift_id]);

  if (!shift) return null;

  const crossesMidnight = endTime <= startTime;

  const handleSave = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await updateShift(shift.shift_id, name.trim(), startTime, endTime, status);
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={shift.name}>
      <DrawerField label="Kode Shift" value={shift.shift_code} />
      <DrawerField label="Cabang" value={branches.find((b) => b.branch_id === shift.branch_id)?.name ?? '—'} />
      <DrawerField label="Jam Kerja (saat ini)" value={shiftWindowLabel(shift)} />
      {canEdit ? (
        <>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <div style={{ flex: 1 }}>
              <FieldLabel label="Nama Shift">
                <TextInput value={name} onChange={(e) => setName(e.target.value)} />
              </FieldLabel>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <div style={{ flex: 1 }}>
              <FieldLabel label="Jam Mulai">
                <TextInput type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </FieldLabel>
            </div>
            <div style={{ flex: 1 }}>
              <FieldLabel label="Jam Selesai">
                <TextInput type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
              </FieldLabel>
            </div>
          </div>
          {crossesMidnight && (
            <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-info)', marginTop: 6 }}>
              Shift lintas hari ({startTime} → {endTime}).
            </div>
          )}
          <div style={{ marginTop: 10 }}>
            <FieldLabel label="Status">
              <Select value={status} onChange={(e) => setStatus(e.target.value as Shift['status'])}>
                <option value="ACTIVE">Aktif</option>
                <option value="INACTIVE">Nonaktif</option>
              </Select>
            </FieldLabel>
          </div>
          <Button variant="primary" onClick={() => void handleSave()} loading={submitting} style={{ marginTop: 12 }}>
            Simpan Perubahan
          </Button>
          {error && <ErrorNote text={error} />}
        </>
      ) : (
        <DrawerField label="Status" value={<StatusBadge status={shift.status} />} />
      )}
    </Drawer>
  );
}
